import { format, getYear, parseISO } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { FundPerformanceNumbers } from '@/api/funds/models';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserDateFormat } from '@/i18n/hooks';

interface Props {
  fundStartDate: string;
  performanceNumbers: FundPerformanceNumbers;
}

export const FundPerformance: FC<Props> = ({
  fundStartDate,
  performanceNumbers,
}) => {
  const texts = useTexts();
  const dateFormat = useUserDateFormat();

  const startYear = getYear(parseISO(fundStartDate));
  const returnsSinceStart = fmsWithTemplate(
    texts.pages.fundList.performance.returnsSinceStart,
    {
      year: startYear.toString(),
    },
  );

  if (!performanceNumbers) {
    return (
      <WithGapWithTopMargin gap="16">
        <StyledSurface
          data-test="fund-performance-ytd"
          isBackground
          lightThemeBackgroundLayer="white"
        >
          <h4 className="font-body-small-demibold">
            {texts.pages.fundList.performance.ytdReturns}
          </h4>
          <div className="font-details">
            {texts.pages.fundList.performance.unavailable}
          </div>
        </StyledSurface>

        <StyledSurface
          data-test="fund-performance-since-start"
          isBackground
          lightThemeBackgroundLayer="white"
        >
          <h4 className="font-body-small-demibold">{returnsSinceStart}</h4>
          <div className="font-details">
            {texts.pages.fundList.performance.unavailable}
          </div>
        </StyledSurface>
      </WithGapWithTopMargin>
    );
  }

  const ytdDate = format(parseISO(performanceNumbers.date), dateFormat);

  return (
    <WithGapWithTopMargin gap="16">
      <StyledSurface data-test="fund-performance-ytd">
        <h4 className="font-body-small-demibold">
          {texts.pages.fundList.performance.ytdReturns}
        </h4>
        <WithGapAlignedBaseline gap="16" direction="row">
          <span className="font-body-demibold">
            {performanceNumbers.yearToDate.toFixed(2)}%
          </span>
          <span className="font-details">{ytdDate}</span>
        </WithGapAlignedBaseline>
      </StyledSurface>

      <StyledSurface data-test="fund-performance-since-start">
        <h4 className="font-body-small-demibold">{returnsSinceStart}</h4>
        <WithGapAlignedBaseline gap="16" direction="row">
          <span className="font-body-demibold">
            {performanceNumbers.sinceStart.toFixed(2)}%
          </span>
          <span className="font-details">
            {texts.pages.fundList.performance.yearlyAverage}
          </span>
        </WithGapAlignedBaseline>
      </StyledSurface>
    </WithGapWithTopMargin>
  );
};

const StyledSurface = styled(ConnectSurface)`
  padding: 8px;
`;

const WithGapWithTopMargin = styled(WithGap)`
  @media (min-width: 769px) {
    // Align with the documents section on larger screens.
    // On smaller screens, the performance section is displayed
    // under the documents section, and does not need additional margin.
    margin-top: 42px;
  }
`;

const WithGapAlignedBaseline = styled(WithGap)`
  align-items: baseline;
`;
